import React from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { Button, Divider, Grid, Typography } from "@material-ui/core";
import PrintIcon from "@material-ui/icons/Print";
import { QRCodeSVG } from "qrcode.react";
import {
  ArrayInput,
  BooleanField,
  BooleanInput,
  FormDataConsumer,
  Labeled,
  NumberInput,
  SimpleFormIterator,
  TextInput,
  maxValue,
  minValue,
} from "react-admin";
import { getPublicOrigin } from "../../../utils/authUtils";
import { DEFAULT_POST_CHECK_IN_MESSAGE } from "../../../utils/driverStops";

/**
 * Driver gate check-in panel (docs/plans/2026-07-18-driver-app.md Task D1).
 * The QR is a plain link to /d/<yardId> — a convenience, not a credential
 * (spec §4.4): nothing to rotate, no device registry.
 */
const QrBlock = ({ label, url }) => (
  <div style={{ textAlign: "center", padding: 8 }}>
    <QRCodeSVG value={url} size={180} includeMargin />
    <Typography variant="subtitle2">{label}</Typography>
    <Typography variant="caption" style={{ wordBreak: "break-all" }}>
      {url}
    </Typography>
  </div>
);

const printQr = (yardName, url, label) => {
  const svg = renderToStaticMarkup(
    <QRCodeSVG value={url} size={512} includeMargin />,
  );
  const w = window.open("", "_blank", "width=700,height=800");
  if (!w) return;
  w.document.write(
    `<html><head><title>${yardName} — driver check-in</title></head>
     <body style="text-align:center;font-family:sans-serif">
       <h1>${yardName}</h1><h2>${label}</h2>${svg}
       <p style="font-size:20px">${url}</p>
       <script>window.print();</script>
     </body></html>`,
  );
  w.document.close();
};

const DriverCheckInPanel = ({ record, disabled }) => {
  if (!record || !record.id) return null;
  const base = `${getPublicOrigin()}/d/${record.id}`;
  const kioskUrl = `${base}?kiosk=1`;
  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography variant="h6">Driver check-in QR</Typography>
        <Typography variant="body2" color="textSecondary">
          Post the driver QR at the gate; open the kiosk link on a shared
          tablet.
        </Typography>
      </Grid>
      <Grid item xs={12} sm={6}>
        <QrBlock label="Driver phones" url={base} />
        <Button
          startIcon={<PrintIcon />}
          fullWidth
          onClick={() => printQr(record.name, base, "Scan to check in")}
        >
          Print gate QR
        </Button>
      </Grid>
      <Grid item xs={12} sm={6}>
        <QrBlock label="Shared tablet (kiosk mode)" url={kioskUrl} />
        <Button
          startIcon={<PrintIcon />}
          fullWidth
          onClick={() => printQr(record.name, kioskUrl, "Kiosk mode")}
        >
          Print kiosk QR
        </Button>
      </Grid>
      {disabled ? (
        <DriverSettingsShow record={record} />
      ) : (
        <DriverSettingsEdit />
      )}
    </Grid>
  );
};

/**
 * The grace period is what turns an appointment into a check-in deadline
 * (scheduledDateTime + grace), so an enabled gate app has to carry one — left
 * blank, driverApi enforces no window at all and a truck three days late still
 * checks itself in. Field-level so the error lands on the input; the same rule
 * is repeated in ReloadYardValidation for saves that never mount this tab.
 *
 * Only an EXPLICIT true is checked, matching the form-level rule and the way
 * driverApi and the Gate Queue menus read the same flag: a yard holding null
 * has never turned the gate on, so it owes no deadline. Counting null as
 * enabled would also refuse the save on every yard whose manager had chosen
 * nothing — this panel ships to all yards, and YardSettingsTab hides inactive
 * tabs with display:none rather than unmounting them, so the validator runs on
 * every My Yard save and would park its error on an input the manager cannot
 * see.
 */
const graceRequiredWhenEnabled = (value, values) =>
  values &&
  values.driverCheckInEnabled === true &&
  (value === null || value === undefined || value === "")
    ? "Required while driver check-in is enabled"
    : undefined;

/**
 * Read-only copy for ReloadYardShow. The show view has no react-final-form
 * <Form> above it, so react-admin inputs throw there ("useField must be used
 * inside of a <Form> component") — same show/edit split as GeoFencePanel.
 */
const DriverSettingsShow = ({ record }) => (
  <>
    <Grid item xs={12} sm={6}>
      <Labeled label="Driver check-in enabled">
        {/* The gate is opt-in, so normalize the flag the way driverApi reads it
            before handing it to BooleanField — a yard that has never been
            configured holds null, and only an explicit true is on. */}
        <BooleanField
          record={{
            ...record,
            driverCheckInEnabled: record?.driverCheckInEnabled === true,
          }}
          source="driverCheckInEnabled"
        />
      </Labeled>
    </Grid>
    <Grid item xs={12} sm={6}>
      <Labeled label="Allow check-in without identifying">
        {/* Shown as the exception it is: the stored flag says "required", and
            driverApi enforces only an explicit true, so null reads as allowed. */}
        <BooleanField
          record={{
            ...record,
            driverCheckInWithoutIdentification:
              record?.driverIdentificationRequired !== true,
          }}
          source="driverCheckInWithoutIdentification"
        />
      </Labeled>
    </Grid>
    <Grid item xs={12} sm={6}>
      <Labeled label="Require driver's license scan at sign-up">
        <BooleanField record={record} source="driverIdScanRequired" />
      </Labeled>
    </Grid>
    <Grid item xs={12} sm={6}>
      <Labeled label="Enforce geo-fence during driver check-in">
        <BooleanField record={record} source="geofenceEnforced" />
      </Labeled>
    </Grid>
    <Grid item xs={12} sm={6}>
      <Labeled label="Allow check-in without an appointment">
        {/* Opt-in like the master switch: a yard that never chose holds null,
            and driverApi reads that as no walk-ins. */}
        <BooleanField
          record={{
            ...record,
            driverCheckInWithoutAppointment:
              record?.driverCheckInWithoutAppointment === true,
          }}
          source="driverCheckInWithoutAppointment"
        />
      </Labeled>
    </Grid>
    <Grid item xs={12} sm={6}>
      <Labeled label="Late check-in grace period">
        <Typography>
          {record?.driverCheckInGraceMinutes != null
            ? `${record.driverCheckInGraceMinutes} min`
            : "—"}
        </Typography>
      </Labeled>
    </Grid>
    <Grid item xs={12}>
      <Labeled label="Driver instructions">
        <Typography style={{ whiteSpace: "pre-wrap" }}>
          {record?.driverInstructions || "—"}
        </Typography>
      </Labeled>
    </Grid>
    <Grid item xs={12}>
      <Labeled label="Post-check-in message">
        <Typography style={{ whiteSpace: "pre-wrap" }}>
          {record?.postCheckInMessage ||
            `— (built-in: ${DEFAULT_POST_CHECK_IN_MESSAGE})`}
        </Typography>
      </Labeled>
    </Grid>
    <Grid item xs={12}>
      <Labeled label="Stops">
        {Array.isArray(record?.driverStops) && record.driverStops.length ? (
          <ol style={{ margin: 0, paddingLeft: 20 }}>
            {record.driverStops.map((stop, index) => (
              <li key={`${stop?.label}-${index}`}>
                <Typography component="span">
                  <strong>{stop?.label}</strong>
                  {stop?.message ? ` — ${stop.message}` : ""}
                </Typography>
              </li>
            ))}
          </ol>
        ) : Array.isArray(record?.driverStops) ? (
          <Typography>
            — (none: no waiting area, check-ins open Called)
          </Typography>
        ) : (
          <Typography>
            — (not yet saved: check-ins wait to be called; saving the yard with
            no stops makes it a yard with no waiting area)
          </Typography>
        )}
      </Labeled>
    </Grid>
  </>
);

/** The stops, in the order the gate sends drivers through them. Optional:
 * a yard with none has no waiting area. Per-row required/duplicate errors
 * come from ReloadYardValidation (validateDriverStops) so a save that never
 * mounts this tab still refuses a half-filled stop. */
const DriverStopsInput = () => (
  <ArrayInput source="driverStops" label="">
    <SimpleFormIterator getItemLabel={(index) => `Stop ${index + 1}`}>
      <FormDataConsumer>
        {({ getSource }) => (
          <Grid container spacing={1}>
            <Grid item xs={12} sm={4}>
              <TextInput
                source={getSource("label")}
                label="Label (dropdown)"
                variant="outlined"
                fullWidth
                helperText="Short — e.g. Scale, Door 4"
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextInput
                source={getSource("message")}
                label="Message to driver"
                variant="outlined"
                fullWidth
                helperText="e.g. Proceed to the scale and wait for the loader"
              />
            </Grid>
          </Grid>
        )}
      </FormDataConsumer>
    </SimpleFormIterator>
  </ArrayInput>
);

/** Owed while the gate is on: it is the one line every driver reads after
 * checking in, and on a yard with no stops their only instruction. Field-level
 * so the error lands on the input; ReloadYardValidation repeats it for saves
 * that never mount this tab. Same explicit-true reading as the grace period. */
const messageRequiredWhenEnabled = (value, values) =>
  values &&
  values.driverCheckInEnabled === true &&
  (!value || !String(value).trim())
    ? "Required while driver check-in is enabled"
    : undefined;

const DriverSettingsEdit = () => (
  <>
    <Grid item xs={12}>
      {/* Master switch, opt-in. A yard that has never been configured for the
          gate holds null, and format() paints that unchecked — the same
          reading driverApi and the Gate Queue menus apply, so this toggle is
          the honest answer to "is the gate running here". */}
      <BooleanInput
        source="driverCheckInEnabled"
        label="Enable driver check-in app"
        format={(v) => v === true}
        helperText="On publishes the gate: drivers check themselves in from the QR or kiosk link, and the trucks queue for the crew on the Gate Queue. Leave it off when the yard checks trucks in at the office instead."
      />
    </Grid>
    <Grid item xs={12}>
      {/* License-image retention is a platform-wide policy (never retained —
            extracted fields only), no longer a per-yard choice. */}
      {/* Inverted over the stored driverIdentificationRequired: on here writes
            false. driverApi enforces only an explicit true, so an unset yard
            (null) is shown on — which is how it already behaves. */}
      <BooleanInput
        source="driverIdentificationRequired"
        label="Allow check-in without identifying"
        format={(v) => v !== true}
        parse={(v) => !v}
        helperText="On lets drivers skip face, phone or driver-code identification and check in with just a typed name. Off makes them identify or sign up before looking up a shipment."
      />
      <BooleanInput
        source="driverIdScanRequired"
        label="Require driver's license scan at sign-up"
        helperText="New drivers must photograph their license to finish sign-up. The photo is never kept — only the extracted details."
      />
      {/* Enforcement needs a boundary to enforce: the geo-fence must be on and
            have yard coordinates (the polygon is generated from them on save).
            Until then the toggle is visible but locked, so the dependency on the
            Geo-fence tab is discoverable instead of silently missing. */}
      <FormDataConsumer>
        {({ formData }) => {
          const geofenceReady = Boolean(
            formData?.geofenceEnabled &&
            formData?.latitude &&
            formData?.longitude,
          );
          return (
            <BooleanInput
              source="geofenceEnforced"
              label="Enforce geo-fence during driver check-in"
              helperText={
                geofenceReady
                  ? "Drivers must share a location inside the boundary to identify and check in. When off, positions are recorded with each check-in but never block it."
                  : "Enable the geo-fence and set the yard coordinates on the Geo-fence tab to use enforcement."
              }
              disabled={!geofenceReady}
            />
          );
        }}
      </FormDataConsumer>
    </Grid>
    <Grid item xs={12}>
      <Divider />
    </Grid>
    <Grid item xs={12}>
      <Typography variant="overline">
        Appointments &amp; instructions
      </Typography>
    </Grid>
    {/* Both settings are stored on the yard; the driver app reads them from
          the session payload, so they only take effect once driverApi returns
          them. */}
    <Grid item xs={12}>
      {/* Walk-ins are off unless the yard says otherwise: an unscheduled truck
          is the arrival nobody planned for, so the driver app sends it to the
          office. Staff schedule the shipment for the day and the driver checks
          in the normal way. format() paints null unchecked, the same reading
          driverApi applies. */}
      <BooleanInput
        source="driverCheckInWithoutAppointment"
        label="Allow check-in without an appointment"
        format={(v) => v === true}
        helperText="On lets drivers self check in a shipment that has no scheduled date and time. Off sends them to see yard staff — schedule the shipment for the day and the driver can then check in as normal."
      />
    </Grid>
    <Grid item xs={12} sm={5}>
      <NumberInput
        source="driverCheckInGraceMinutes"
        label="Late check-in grace period (minutes)"
        variant="outlined"
        fullWidth
        validate={[graceRequiredWhenEnabled, minValue(0), maxValue(1440)]}
        inputProps={{ step: 5, min: 0, max: 1440 }}
        helperText="Minutes past the appointment time a driver may still self check in. Later than that, drivers are sent to see yard staff. 0 means no grace at all."
      />
    </Grid>
    <Grid item xs={12}>
      <TextInput
        source="driverInstructions"
        label="Driver instructions"
        variant="outlined"
        fullWidth
        multiline
        rows={4}
        helperText="Shown to drivers during check-in — gate directions, speed limit, PPE, where to wait."
      />
    </Grid>
    <Grid item xs={12}>
      <Divider />
    </Grid>
    <Grid item xs={12}>
      <Typography variant="overline">After check-in</Typography>
      <Typography variant="body2" color="textSecondary">
        The message is what the driver reads the moment they have checked in.
        The stops are optional: list them and drivers wait to be called, the
        Gate Queue&apos;s stop dropdown offers them, calling a driver sends them
        to the first, and the crew place the load once the gate has sent them to
        the last. List none and there is no waiting — every check-in opens
        Called, the message is the driver&apos;s instruction (say where to go),
        and the crew can place the load at once.
      </Typography>
    </Grid>
    <Grid item xs={12}>
      <TextInput
        source="postCheckInMessage"
        label="Post-check-in message"
        variant="outlined"
        fullWidth
        multiline
        rows={2}
        validate={messageRequiredWhenEnabled}
        helperText={`With stops: where to wait until called. Without: where to go.`}
      />
    </Grid>
    <Grid item xs={12}>
      <Typography variant="subtitle2">Stops (optional)</Typography>
      <DriverStopsInput />
    </Grid>
  </>
);

export default DriverCheckInPanel;
